import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Pagination, Stack, Typography } from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import ModalViewer from '@product-framework/components/Viewers/ModalViewer';
import { isSupportedByModalViewer } from '@product-framework/components/Viewers';
import {
  getFileExtension,
  SnackbarApiErrorMessage,
  useDialog,
  useLocalization,
} from '@ui-framework/index';
import {
  getIssueAttachmentDownload,
  getIssueAttachments,
  removeIssueAttachment,
  uploadIssueAttachments,
} from './api';
import AttachmentCard from './AttachmentCard';
import QueuedAttachmentList from './QueuedAttachmentList';

const ATTACHMENTS_PER_PAGE = 5;

function IssueAttachments({
  issueId,
  dataProvider,
  canManageAttachments = false,
  fileExtensions = [],
  onChanged = () => undefined,
  allowRemove = true,
  showTitle = false,
}) {
  const [attachments, setAttachments] = useState([]);
  const [attachmentPage, setAttachmentPage] = useState(1);
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [viewer, setViewer] = useState(null);
  const { confirmationRef } = useDialog();
  const { formatMessage } = useLocalization();

  const loadAttachments = async () => {
    if (!issueId) return;
    setLoading(true);
    const result = await getIssueAttachments({ dataProvider, issueId });
    setLoading(false);
    if (result.ok) {
      setAttachments(result.data?.data || []);
      setError(null);
      return;
    }
    setError(
      result?.errors || [
        {
          message: formatMessage({
            id: 'COULD_NOT_LOAD_ATTACHMENTS',
            defaultMessage: 'Could not load attachments',
          }),
        },
      ],
    );
  };

  useEffect(() => {
    loadAttachments();
  }, [issueId]);

  const attachmentPageCount = Math.max(
    1,
    Math.ceil(attachments.length / ATTACHMENTS_PER_PAGE),
  );
  const visibleAttachments = useMemo(() => {
    const start = (attachmentPage - 1) * ATTACHMENTS_PER_PAGE;
    return attachments.slice(start, start + ATTACHMENTS_PER_PAGE);
  }, [attachmentPage, attachments]);

  useEffect(() => {
    setAttachmentPage(current => Math.min(current, attachmentPageCount));
  }, [attachmentPageCount]);

  const addFiles = files => {
    setQueuedFiles(current => [
      ...current,
      ...files.map(file => ({ file, attachmentType: 'OTHER' })),
    ]);
  };

  const uploadQueuedFiles = async () => {
    setUploading(true);
    const uploadResults = await uploadIssueAttachments({
      dataProvider,
      issueId,
      files: queuedFiles,
    });
    const failures = uploadResults.flatMap(result =>
      result.ok
        ? []
        : result.errors || [
            {
              message: formatMessage(
                {
                  id: 'COULD_NOT_UPLOAD_FILE_NAME',
                  defaultMessage: 'Could not upload {fileName}',
                },
                { fileName: result.fileName },
              ),
            },
          ],
    );
    setUploading(false);

    if (failures.length) {
      setError(failures);
      return false;
    }

    setQueuedFiles([]);
    await loadAttachments();
    onChanged();
    return true;
  };

  const getAttachmentUrl = async attachment => {
    const result = await getIssueAttachmentDownload({
      dataProvider,
      issueId,
      attachmentId: attachment.id,
    });
    if (result.ok && result.data?.url) {
      return result.data.url;
    }
    setError(
      result?.errors || [
        {
          message: formatMessage({
            id: 'COULD_NOT_DOWNLOAD_ATTACHMENT',
            defaultMessage: 'Could not download attachment',
          }),
        },
      ],
    );
    return null;
  };

  const downloadAttachment = async attachment => {
    const url = await getAttachmentUrl(attachment);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const viewAttachment = async attachment => {
    const url = await getAttachmentUrl(attachment);
    if (!url) return;
    if (!isSupportedByModalViewer(attachment.fileName)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setViewer({ attachment, url });
  };

  const deleteAttachment = attachment => {
    confirmationRef.current.init({
      title: formatMessage({
        id: 'DELETE_ATTACHMENT',
        defaultMessage: 'Delete Attachment',
      }),
      message: formatMessage({
        id: 'DO_YOU_WANT_TO_DELETE_ATTACHMENT',
        defaultMessage: 'Do you want to delete this attachment?',
      }),
      positiveButtonText: formatMessage({ id: 'YES', defaultMessage: 'Yes' }),
      negativeButtonText: formatMessage({
        id: 'CANCEL',
        defaultMessage: 'Cancel',
      }),
      positiveButtonIcon: <IconLibrary.Delete />,
      negativeButtonIcon: <IconLibrary.Cancel />,
      loadingMessage: formatMessage({
        id: 'DELETING_ATTACHMENT',
        defaultMessage: 'Deleting attachment...',
      }),
      onChange: async value => {
        if (!value) {
          confirmationRef.current.setOpen(false);
          return;
        }

        confirmationRef.current.setLoading(true);
        const result = await removeIssueAttachment({
          dataProvider,
          issueId,
          attachmentId: attachment.id,
        });
        confirmationRef.current.setLoading(false);
        confirmationRef.current.setOpen(false);

        if (result.ok) {
          await loadAttachments();
          onChanged();
          return;
        }
        setError(
          result?.errors || [
            {
              message: formatMessage({
                id: 'COULD_NOT_REMOVE_ATTACHMENT',
                defaultMessage: 'Could not remove attachment.',
              }),
            },
          ],
        );
      },
    });
    confirmationRef.current.setOpen(true);
  };

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6">
          {formatMessage({
            id: 'ATTACHMENTS',
            defaultMessage: 'Attachments',
          })}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          <SnackbarApiErrorMessage errors={error} />
        </Alert>
      )}
      {canManageAttachments && (
        <QueuedAttachmentList
          files={queuedFiles}
          onAddFiles={addFiles}
          onRemoveFile={index =>
            setQueuedFiles(current => current.filter((_, idx) => idx !== index))
          }
          onChangeType={(index, attachmentType) =>
            setQueuedFiles(current =>
              current.map((item, idx) =>
                idx === index ? { ...item, attachmentType } : item,
              ),
            )
          }
          formatMessage={formatMessage}
          fileExtensions={fileExtensions}
          uploading={uploading}
          onUpload={uploadQueuedFiles}
        />
      )}
      <Box sx={{ mt: 2 }}>
        {loading && (
          <Typography>
            {formatMessage({
              id: 'LOADING_ATTACHMENTS',
              defaultMessage: 'Loading attachments...',
            })}
          </Typography>
        )}
        {!loading && attachments.length === 0 && (
          <Typography color="text.secondary">
            {formatMessage({
              id: 'NO_ATTACHMENTS_YET',
              defaultMessage: 'No attachments yet',
            })}
          </Typography>
        )}
        <Stack spacing={1}>
          {visibleAttachments.map(attachment => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              canRemove={allowRemove && canManageAttachments}
              formatMessage={formatMessage}
              onView={viewAttachment}
              onDownload={downloadAttachment}
              onDelete={deleteAttachment}
            />
          ))}
        </Stack>
        {attachments.length > ATTACHMENTS_PER_PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={attachmentPageCount}
              page={attachmentPage}
              onChange={(_, page) => setAttachmentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Box>
      {viewer && (
        <ModalViewer
          open
          onClose={() => setViewer(null)}
          title={viewer.attachment.fileName}
          fileType={getFileExtension(viewer.attachment.fileName)}
          fileUrl={viewer.url}
        />
      )}
    </Box>
  );
}

export default IssueAttachments;
